/**
 * Accountant Messages Page — Select a client, then chat.
 * Shows all linked clients with per-client unread message counts.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  MessageSquare, Send, Loader2, Paperclip, X, Users, ArrowLeft, Search, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Client List Panel ── */
const ClientList = ({ clients, selectedId, onSelect, loading, searchQuery, setSearchQuery }) => {
  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-slate-700">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search clients..."
            className="bg-slate-900 border-slate-600 text-white h-8 text-xs pl-8"
            data-testid="client-search-input"
          />
        </div>
      </div>

      {/* Client List */}
      <div className="flex-1 overflow-y-auto" data-testid="client-list">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center">
            <Users className="h-8 w-8 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-500 text-xs">
              {clients.length === 0 ? 'No linked clients yet' : 'No clients match your search'}
            </p>
          </div>
        ) : (
          filtered.map(client => (
            <button
              key={client.user_id}
              onClick={() => onSelect(client)}
              className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors border-b border-slate-700/50 ${
                selectedId === client.user_id
                  ? 'bg-sky-600/10 border-l-2 border-l-sky-500'
                  : 'hover:bg-slate-700/30 border-l-2 border-l-transparent'
              }`}
              data-testid={`client-item-${client.user_id}`}
            >
              <div className="w-8 h-8 rounded-full bg-sky-600/20 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-sky-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${selectedId === client.user_id ? 'text-sky-300' : 'text-white'}`}>
                  {client.name}
                </p>
                <p className="text-[11px] text-slate-500 truncate">{client.email}</p>
              </div>
              {client.unread_messages > 0 && (
                <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-5 min-w-[20px] flex items-center justify-center" data-testid={`unread-badge-${client.user_id}`}>
                  {client.unread_messages}
                </Badge>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

/* ── Chat Panel ── */
const ChatPanel = ({ client, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachFile, setAttachFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/messages/${client.user_id}`);
      setMessages(res.data.messages || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.user_id]);

  useEffect(() => {
    setLoading(true);
    fetchMessages();
    const iv = setInterval(fetchMessages, 8000);
    return () => clearInterval(iv);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setAttachFile({ name: file.name, content: reader.result.split(',')[1], type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() && !attachFile) return;
    setSending(true);
    try {
      let attachmentId = null;
      let attachmentType = null;

      if (attachFile) {
        setUploading(true);
        const docRes = await axios.post(`${API_URL}/api/documents/upload`, {
          file_name: attachFile.name,
          file_content: attachFile.content,
          document_type: 'other',
          tax_year: '',
          month: ''
        });
        attachmentId = docRes.data.document?.id;
        attachmentType = 'document';
        setUploading(false);
      }

      await axios.post(`${API_URL}/api/accountant/messages`, {
        client_user_id: client.user_id,
        content: newMsg.trim() || (attachFile ? `Shared file: ${attachFile.name}` : ''),
        attachment_type: attachmentType,
        attachment_id: attachmentId
      });
      setNewMsg('');
      setAttachFile(null);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); setUploading(false); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700 bg-slate-800/50">
        <button onClick={onBack} className="lg:hidden text-slate-400 hover:text-white" data-testid="back-to-clients-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="w-8 h-8 rounded-full bg-sky-600/20 flex items-center justify-center shrink-0">
          <User className="h-4 w-4 text-sky-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{client.name}</p>
          <p className="text-[11px] text-slate-500">{client.email}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <MessageSquare className="h-10 w-10 text-slate-600 mb-3" />
            <p className="text-slate-500 text-sm">No messages with {client.name} yet.</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.sender_role === 'accountant' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 ${
                msg.sender_role === 'accountant'
                  ? 'bg-sky-600/20 border border-sky-500/20'
                  : 'bg-slate-700/50 border border-slate-600/50'
              }`}>
                <p className="text-xs text-slate-500 mb-0.5">
                  {msg.sender_role === 'accountant' ? 'You' : msg.sender_name || client.name}
                </p>
                <p className="text-sm text-slate-200">{msg.content}</p>
                {msg.attachment_id && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-sky-400">
                    <Paperclip className="h-3 w-3" />
                    <span>File attached</span>
                  </div>
                )}
                <p className="text-[10px] text-slate-600 mt-1">
                  {new Date(msg.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Send Form */}
      <form onSubmit={sendMessage} className="border-t border-slate-700 p-3 flex items-end gap-2">
        <div className="flex-1 space-y-1">
          {attachFile && (
            <div className="flex items-center gap-2 bg-slate-700/50 rounded px-2 py-1 text-xs text-slate-300">
              <Paperclip className="h-3 w-3 text-sky-400" />
              <span className="truncate flex-1">{attachFile.name}</span>
              <button type="button" onClick={() => setAttachFile(null)} className="text-slate-500 hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input value={newMsg} onChange={e => setNewMsg(e.target.value)}
              placeholder={`Message ${client.name}...`} disabled={sending}
              className="bg-slate-900 border-slate-600 text-white h-9 text-sm flex-1"
              data-testid="accountant-message-input" />
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
            <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}
              className="text-slate-400 hover:text-sky-400 h-9 px-2" data-testid="accountant-attach-file-btn">
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button type="submit" size="sm" disabled={sending || (!newMsg.trim() && !attachFile)}
              className="bg-sky-600 hover:bg-sky-700 h-9 px-3" data-testid="accountant-send-message-btn">
              {sending || uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

/* ── Main Page ── */
const AccountantMessages = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/clients`);
      setClients(res.data.clients || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    const iv = setInterval(fetchClients, 15000);
    return () => clearInterval(iv);
  }, [fetchClients]);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
  };

  const handleBack = () => {
    setSelectedClient(null);
    fetchClients(); // refresh unread counts
  };

  return (
    <div className="space-y-4" data-testid="accountant-messages-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-sky-400" />Messages
        </h1>
        <p className="text-slate-400 mt-1 text-sm">
          {clients.length > 0 ? `${clients.length} linked client${clients.length !== 1 ? 's' : ''}` : 'No linked clients'}
        </p>
      </div>

      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <CardContent className="p-0">
          <div className="flex h-[calc(100vh-220px)] min-h-[500px]">
            {/* Left: Client List — always show on desktop, hide on mobile when chat is open */}
            <div className={`w-full lg:w-80 border-r border-slate-700 shrink-0 ${selectedClient ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'}`}>
              <ClientList
                clients={clients}
                selectedId={selectedClient?.user_id}
                onSelect={handleSelectClient}
                loading={loading}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
            </div>

            {/* Right: Chat Panel */}
            <div className={`flex-1 flex flex-col ${selectedClient ? '' : 'hidden lg:flex'}`}>
              {selectedClient ? (
                <ChatPanel client={selectedClient} onBack={handleBack} />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                  <MessageSquare className="h-12 w-12 text-slate-600 mb-3" />
                  <h3 className="text-lg font-semibold text-slate-400 mb-1">Select a client</h3>
                  <p className="text-slate-500 text-sm">Choose a client from the list to start messaging</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantMessages;
